import { Component, Input, OnInit } from '@angular/core';
import { BenefitInvoice } from '@models/benefits/benefit-invoice.model';
import { environment } from '@env/environment';
import { HttpClient } from '@angular/common/http';
import { JsonApiBody, JsonApiIndexResponse } from '@app/interfaces';
import { UnmappedEmployees } from '@benefits/interfaces/benefit-invoice-unmapped-employees.interface';
import { AuthService, NotifyService } from '@app/services';
import { BenefitPlan as EmployeeBenefitPlan } from '@models/employee/benefits/benefit-plan.model';
import { EmployeeBenefitPlans } from '@models/benefits/employee-benefit-plans.model';
import { PlatformModule } from '@app/platform.module';
import { BannerVariant } from '@app/components/platform/banner/banner.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { BenefitPlan } from '@models/benefits/benefit-plan.model';
import moment from 'moment/moment';

type EmployeeSelection = {
    disabled: boolean;
    employeeBenefitPlan: EmployeeBenefitPlan;
};

@Component({
    selector: 'app-benefit-invoice-unmapped-employees',
    templateUrl: './benefit-invoice-unmapped-employees.template.html',
    styleUrls: ['./benefit-invoice-unmapped-employees.style.scss'],
    standalone: true,
    imports: [PlatformModule, TranslateModule, NgTemplateOutlet],
})
export class BenefitInvoiceUnmappedEmployeesComponent implements OnInit {
    @Input() benefitInvoice!: BenefitInvoice;

    unmappedEmployees: JsonApiBody<UnmappedEmployees>[] = [];
    employeeChoices?: Map<number, EmployeeSelection>;

    bannerVariant: BannerVariant = 'info';
    bannerMessage!: string;
    isLoading = true;

    mappingDialogOpen = false;

    shouldShow = false;

    constructor(
        private http: HttpClient,
        private auth: AuthService,
        private notify: NotifyService,
        private translateService: TranslateService
    ) {}

    async ngOnInit(): Promise<void> {
        // We're only running these checks when the invoice is unsynced because the
        // user can't sync the invoice more than once.
        this.shouldShow = this.benefitInvoice?.isPersisted && this.benefitInvoice?.status === 'unsynced';
        if (!this.shouldShow) {
            return;
        }

        this.bannerMessage = this.translateService.instant('benefits.benefitsPayroll.checkingForUnmappedEmployees');

        this.unmappedEmployees = await this.getUnmappedEmployees();

        if (!this.unmappedEmployees.length) {
            this.bannerVariant = 'success';
            this.bannerMessage = this.translateService.instant('benefits.benefitsPayroll.allEmployeesWereMapped');
            this.isLoading = false;
            return;
        }

        const requests = await this.getRequestsToRetrieveEmployeeBenefitPlans();

        if (!requests) {
            this.bannerVariant = 'warning';
            this.bannerMessage = this.translateService.instant(
                'benefits.benefitsPayroll.employeesonThisStatementRemainUnmapped',
                { count: this.unmappedEmployees.length }
            );
            this.isLoading = false;

            return;
        }

        await this.generateEmployeeBenefitPlanMap(requests);

        if (!this.employeeChoices?.size) {
            this.bannerVariant = 'warning';
            this.bannerMessage = this.translateService.instant(
                'benefits.benefitsPayroll.employeesonThisStatementRemainUnmapped',
                { count: this.unmappedEmployees.length }
            );
            this.isLoading = false;

            return;
        }

        this.bannerVariant = 'warning';
        this.bannerMessage = this.translateService.instant(
            'benefits.benefitsPayroll.employeesCouldNotBeMappedPleaseMap',
            { count: this.unmappedEmployees.length }
        );

        this.isLoading = false;
    }

    associateToEmployee(certificateNumber: string, employeeSelection: EmployeeSelection | null): void {
        if (employeeSelection !== null) {
            employeeSelection.disabled = true;
            employeeSelection.employeeBenefitPlan.certificateNumber = certificateNumber;

            this.saveEmpoyeeBenefitPlan(employeeSelection.employeeBenefitPlan);

            return;
        }

        if (this.employeeChoices) {
            for (const employeeChoice of this.employeeChoices?.values()) {
                if (employeeChoice.employeeBenefitPlan.certificateNumber === certificateNumber) {
                    employeeChoice.employeeBenefitPlan.certificateNumber = null;
                    employeeChoice.disabled = false;

                    this.saveEmpoyeeBenefitPlan(employeeChoice.employeeBenefitPlan);
                }
            }
        }
    }

    private saveEmpoyeeBenefitPlan(employeeBenefitPlan: EmployeeBenefitPlan) {
        try {
            employeeBenefitPlan.param('employee', employeeBenefitPlan.employeeId).save();
        } catch (error) {
            this.notify.error('benefits.benefitsPayroll.somethingWentWrong');
        }
    }

    /**
     * Generates a map of employee benefit plan assignment records that will be to drive a list of options.
     * These will be the records that the user will be mapping invoice records to since this is where the
     * certificate number value lives.
     */
    private async generateEmployeeBenefitPlanMap(requests: Promise<[EmployeeBenefitPlans[], any]>[]) {
        const employeeBenefitPlansResults = await Promise.all(requests);

        const allEmployeeBenefitPlanRecords = employeeBenefitPlansResults.flatMap((item) =>
            item[0].map((item) => new EmployeeBenefitPlan(item.getAttributes()))
        );

        this.employeeChoices = new Map();
        allEmployeeBenefitPlanRecords.forEach((item) =>
            this.employeeChoices?.set(item.employeeId, { disabled: false, employeeBenefitPlan: item })
        );
    }

    /**
     * Returns a list of promises that loads all employee assignments to benefit plans where the employee assignment
     * does not have a certificate number.
     */
    private async getRequestsToRetrieveEmployeeBenefitPlans() {
        const [benefitPlans] = await BenefitPlan.param('company', this.auth.company.id).all();

        const requests: Promise<[EmployeeBenefitPlans[], any]>[] = [];

        benefitPlans.forEach((benefitPlan: BenefitPlan) => {
            requests.push(
                EmployeeBenefitPlans.param('benefitPlan', benefitPlan.id)
                    .where('certificateNumber', null)
                    .where('effective_at', {
                        to: moment(new Date()).format('YYYY-MM-DD'),
                    })
                    .with(['simpleEmployee', 'simpleEmployee.account'])
                    .all()
            );
        });

        return requests;
    }

    /**
     * Determines if any records in the uploaded invoice couldn't be automatically mapped to an employee in humi.
     */
    private async getUnmappedEmployees(): Promise<JsonApiBody<UnmappedEmployees>[]> {
        const endpoint = `${environment.api}/v2/benefitManagement/getUnmappedEmployeesFromBenefitInvoice`;
        const payload = {
            benefitInvoiceId: this.benefitInvoice.id,
        };

        return this.http
            .post(endpoint, payload)
            .toPromise()
            .then((response: JsonApiIndexResponse<JsonApiBody<UnmappedEmployees>>) => response.data);
    }
}
